import {
	IMAGE_EXTENSIONS,
	IMAGE_MIME_TYPE,
	QUERY_KEYS,
} from "@/libraries/constant";
import { isFile, randFileName } from "@/libraries/helper";
import type { PresignPayloadSchema } from "@/schemas";
import { fileService } from "@/services";
import { useGalleryStore } from "@/stores";
import type { CreateStorageResponse, FooFn, ResError } from "@/types";
import { type MutateOptions, useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";

function useGallery() {
	const [currentImage, setCurrentImage] = useState<number>(0);

	const mutateUpload = useMutation<
		CreateStorageResponse,
		ResError,
		PresignPayloadSchema[]
	>({
		mutationKey: [QUERY_KEYS.MUTATE_UPLOAD_IMAGES],
	});

	const images = useGalleryStore((state) => state.images);
	const addImages = useGalleryStore((state) => state.addImages);
	const updateImages = useGalleryStore((state) => state.updateImages);
	const removeImages = useGalleryStore((state) => state.removeImages);
	const resetImages = useGalleryStore((state) => state.reset);

	const onViewImage = (index: number) => {
		return setCurrentImage(index);
	};

	const onCaptureImage = useCallback(
		async (file: string | File) => {
			let fileImage = file as File;
			if (!isFile(file)) {
				fileImage = await fileService.stringToFile(
					file,
					randFileName(IMAGE_EXTENSIONS.JPEG),
					IMAGE_MIME_TYPE.JPEG,
				);
			}
			return addImages(fileImage);
		},
		[addImages],
	);

	const onFinishEdit = useCallback(
		async (imageStr: string, currentIdx: number) => {
			const currentFile = images[currentIdx];
			const newFile = await fileService.stringToFile(
				imageStr,
				currentFile.name,
				currentFile.type,
			);
			return updateImages(currentIdx, newFile);
		},
		[images, updateImages],
	);

	const onRemoveImage = useCallback(
		(index: number) => {
			removeImages(index);
			return setCurrentImage(0);
		},
		[removeImages],
	);

	const onUploadImages = useCallback(
		(
			options:
				| MutateOptions<
						CreateStorageResponse,
						ResError,
						{
							file_name: string;
							file?: any;
						}[],
						unknown
				  >
				| undefined,
		) => {
			const files = images.map<PresignPayloadSchema>((img) => ({
				file: img,
				file_name: img.name,
			}));
			return mutateUpload.mutate(files, {
				...options,
				onSuccess: (res, variables, ctx) => {
					resetImages();
					return options?.onSuccess?.(res, variables, ctx);
				},
			});
		},
		[images, resetImages, mutateUpload],
	);

	const onResetImages = useCallback(() => {
		setCurrentImage(0);
		return resetImages();
	}, [resetImages]);

	return {
		isUploadLoading: mutateUpload.isPending,
		images,
		image: images[currentImage],
		currentImage,
		onCaptureImage,
		onViewImage,
		onFinishEdit,
		onRemoveImage,
		onUploadImages,
		onResetImages,
	};
}

export default useGallery;
